import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowLeft,
  FiCheckCircle,
  FiMapPin,
  FiPackage,
  FiStar,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import "./ActividadesPanel.css";

/*
 * Entretenimiento: deportes y actividades que la persona puede probar.
 *
 * Arriba, lo que encaja con su ficha y POR QUÉ encaja, que es lo único que
 * mueve a alguien a probar algo nuevo. Debajo, el catálogo entero por
 * categorías para fisgonear.
 */

const auth = async () => ({ Authorization: `Bearer ${(await getSession()).token}` });

const INTENSIDAD = { suave: "Suave", media: "Media", alta: "Alta" };
const IMPACTO = {
  bajo: "No castiga articulaciones",
  medio: "Impacto moderado",
  alto: "Mucho impacto",
};
const SOCIAL = { individual: "Se hace solo", grupo: "Con gente", ambos: "Solo o con gente" };

function Tarjeta({ act, onAbrir, destacada }) {
  return (
    <button type="button" className={destacada ? "ap-tarjeta ap-tarjeta-top" : "ap-tarjeta"} onClick={() => onAbrir(act)}>
      {act.razon && (
        <span className="ap-razon">
          <FiStar aria-hidden="true" /> {act.razon}
        </span>
      )}
      {act.choca?.length > 0 && (
        <span className="ap-choca">
          <FiAlertTriangle aria-hidden="true" /> Consúltalo antes: {act.choca.join(", ")}
        </span>
      )}
      <h3>{act.nombre}</h3>
      <p>{act.resumen}</p>
      <dl className="ap-mini">
        <div>
          <dt>Gasto</dt>
          <dd>
            {act.kcalHora[0]}-{act.kcalHora[1]} kcal/h
          </dd>
        </div>
        <div>
          <dt>Intensidad</dt>
          <dd>{INTENSIDAD[act.intensidad]}</dd>
        </div>
        <div>
          <dt>Articulaciones</dt>
          <dd className={act.impacto === "bajo" ? "ap-bien" : act.impacto === "alto" ? "ap-ojo" : ""}>
            {IMPACTO[act.impacto]}
          </dd>
        </div>
      </dl>
    </button>
  );
}

function Detalle({ act, onVolver }) {
  return (
    <article className="ap-detalle">
      <button type="button" className="ap-volver" onClick={onVolver}>
        <FiArrowLeft /> Todas las actividades
      </button>

      <header>
        <h2>{act.nombre}</h2>
        <p>{act.resumen}</p>
      </header>

      {act.choca?.length > 0 && (
        <div className="ap-aviso">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <strong>Habla con tu entrenador antes de empezar</strong>
            <p>
              Esta actividad carga {act.choca.join(" y ")}, y es justo lo que has
              dicho que te molesta. No significa que no puedas hacerla: significa
              que conviene adaptarla antes.
            </p>
          </div>
        </div>
      )}

      <section className="ap-porque">
        <h3>Por qué va bien</h3>
        <p>{act.porQue}</p>
      </section>

      <div className="ap-numeros">
        {[
          ["Gasto por hora", `${act.kcalHora[0]}-${act.kcalHora[1]} kcal`],
          ["Intensidad", INTENSIDAD[act.intensidad]],
          ["Articulaciones", IMPACTO[act.impacto]],
          ["Temporada", act.temporada],
        ].map(([t, v]) => (
          <div key={t}>
            <span>{t}</span>
            <strong>{v}</strong>
          </div>
        ))}
      </div>

      <section className="ap-bloque">
        <h3>
          <FiActivity aria-hidden="true" /> Qué te mejora
        </h3>
        <ul className="ap-etis">
          {act.bueno.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </section>

      <section className="ap-bloque">
        <h3>
          <FiCheckCircle aria-hidden="true" /> Cómo empezar
        </h3>
        <p>{act.empezar}</p>
      </section>

      <div className="ap-practico">
        <div>
          <FiPackage aria-hidden="true" />
          <span>Qué necesitas</span>
          <p>{act.material}</p>
        </div>
        <div>
          <FiMapPin aria-hidden="true" />
          <span>Dónde</span>
          <p>{act.donde}</p>
        </div>
        <div>
          <FiUsers aria-hidden="true" />
          <span>Compañía</span>
          <p>{SOCIAL[act.social]}</p>
        </div>
      </div>

      <p className="ap-nota">
        Las kilocalorías son una orientación para comparar actividades, no una
        medición: dependen de tu peso y de cuánto rato estés de verdad en
        movimiento.
      </p>
    </article>
  );
}

function ActividadesPanel() {
  const [reco, setReco] = useState(null);
  const [todas, setTodas] = useState(null);
  const [categorias, setCategorias] = useState({});
  const [abierta, setAbierta] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    try {
      const headers = await auth();
      const [r, t] = await Promise.all([
        axios.get(`${API_URL}/actividades/para-mi`, { headers }),
        axios.get(`${API_URL}/actividades`, { headers }),
      ]);
      setReco(r.data);
      setTodas(t.data.actividades);
      setCategorias(t.data.categorias);
      setError("");
    } catch {
      setError("No se pudieron cargar las actividades.");
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Las razones y los avisos calculados para mí, aplicados al catálogo entero
  const enriquecidas = useMemo(() => {
    if (!todas) return [];
    const extras = {};
    for (const a of [...(reco?.paraTi || []), ...(reco?.conCuidado || [])]) {
      extras[a.id] = { razon: a.razon, choca: a.choca };
    }
    return todas.map((a) => ({ ...a, ...extras[a.id] }));
  }, [todas, reco]);

  const visibles = useMemo(
    () => (filtro === "todas" ? enriquecidas : enriquecidas.filter((a) => a.categoria === filtro)),
    [enriquecidas, filtro]
  );

  if (error) return <div className="app-estado app-estado-error">{error}</div>;
  if (!reco || !todas) return <div className="app-estado">Cargando actividades…</div>;
  if (abierta) return <Detalle act={abierta} onVolver={() => setAbierta(null)} />;

  return (
    <div className="ap">
      <header className="ap-intro">
        <h2>Prueba algo que te divierta</h2>
        <p>
          Lo que de verdad hace que alguien siga entrenando dentro de tres años
          no es la fuerza de voluntad: es haber encontrado algo que le apetece
          hacer. Aquí tienes {todas.length} opciones, con lo que cada una te
          aporta y lo que hace falta para empezar.
        </p>
      </header>

      {/* Recomendadas */}
      <section className="ap-reco">
        <h3>
          <FiZap aria-hidden="true" />
          {reco.personalizado ? "Para ti, según tu ficha" : "Para empezar a mirar"}
        </h3>
        {!reco.personalizado && (
          <p className="ap-sinficha">
            Esto todavía no está personalizado. Rellena tu ficha y aquí
            aparecerán las que encajan con tu objetivo, tu nivel y lo que te
            molesta.
          </p>
        )}
        <div className="ap-rejilla">
          {reco.paraTi.map((a) => (
            <Tarjeta key={a.id} act={a} onAbrir={setAbierta} destacada />
          ))}
        </div>
      </section>

      {/* Lo que choca con alguna molestia: se dice, no se esconde */}
      {reco.conCuidado.length > 0 && (
        <section className="ap-reco">
          <h3>
            <FiAlertTriangle aria-hidden="true" /> Te encajarían, pero coméntalas antes
          </h3>
          <p className="ap-sinficha">
            Estas van bien con tu objetivo, pero cargan{" "}
            {reco.zonasDetectadas.join(", ")}, que es lo que has dicho que te
            molesta. Casi siempre se pueden adaptar: pregúntale a tu entrenador.
          </p>
          <div className="ap-rejilla">
            {reco.conCuidado.map((a) => (
              <Tarjeta key={a.id} act={a} onAbrir={setAbierta} />
            ))}
          </div>
        </section>
      )}

      {/* Catálogo */}
      <section className="ap-reco">
        <h3>Todas las actividades</h3>
        <div className="ap-filtros">
          <button
            type="button"
            className={filtro === "todas" ? "ap-filtro ap-filtro-on" : "ap-filtro"}
            onClick={() => setFiltro("todas")}
          >
            Todas
          </button>
          {Object.entries(categorias).map(([id, c]) => (
            <button
              key={id}
              type="button"
              className={filtro === id ? "ap-filtro ap-filtro-on" : "ap-filtro"}
              onClick={() => setFiltro(id)}
            >
              <span aria-hidden="true">{c.icono}</span> {c.nombre}
            </button>
          ))}
        </div>
        <div className="ap-rejilla">
          {visibles.map((a) => (
            <Tarjeta key={a.id} act={a} onAbrir={setAbierta} />
          ))}
        </div>
      </section>
    </div>
  );
}

export default ActividadesPanel;
